/**
 * Пакеты между игроками — двоичные, строгий разбор: всё, что не сходится по длине и полям, отбрасывается.
 * По сети ходят только нажатия, а не картинка и не положения (docs/design/netcode.md).
 */
export const PROTOCOL_VERSION = 1;
export const MAX_INPUTS_PER_PACKET = 16;

export const MESSAGE = { hello: 1, inputs: 2, ping: 3, pong: 4 } as const;

export interface HelloMessage {
  readonly type: "hello";
  readonly version: number;
  /** Хеш содержимого (способности, герои, правила) и сборки: у обоих должен совпасть. */
  readonly content: number;
  readonly seed: number;
}

export interface InputsMessage {
  readonly type: "inputs";
  /** Кадр первого нажатия в пакете; дальше — подряд. */
  readonly start: number;
  readonly inputs: readonly number[];
  /** До какого кадра включительно отправитель получил наши нажатия. */
  readonly ack: number;
  /** Текущий кадр отправителя — для выравнивания часов. */
  readonly frame: number;
  /** На сколько кадров отправитель, по его оценке, впереди нас (со сдвигом на задержку сети). */
  readonly lead: number;
  /** Сумма подтверждённого кадра checksumFrame (или -1). */
  readonly checksumFrame: number;
  readonly checksum: number;
}

/** Прогрев связи до раунда: метка времени отправителя, ответ возвращает её как есть. */
export interface PingMessage {
  readonly type: "ping" | "pong";
  readonly stamp: number;
}

export type NetMessage = HelloMessage | InputsMessage | PingMessage;

export function encode(message: NetMessage): Uint8Array {
  if (message.type !== "hello" && message.type !== "inputs") {
    const view = new DataView(new ArrayBuffer(5));
    view.setUint8(0, MESSAGE[message.type]);
    view.setUint32(1, message.stamp >>> 0);
    return new Uint8Array(view.buffer);
  }
  if (message.type === "hello") {
    const view = new DataView(new ArrayBuffer(13));
    view.setUint8(0, MESSAGE.hello);
    view.setUint32(1, message.version);
    view.setUint32(5, message.content >>> 0);
    view.setInt32(9, message.seed | 0);
    return new Uint8Array(view.buffer);
  }
  const count = Math.min(message.inputs.length, MAX_INPUTS_PER_PACKET);
  const view = new DataView(new ArrayBuffer(23 + count * 2));
  view.setUint8(0, MESSAGE.inputs);
  view.setInt32(1, message.start);
  view.setUint8(5, count);
  for (let index = 0; index < count; index++) view.setUint16(6 + index * 2, (message.inputs[index] ?? 0) & 0xffff);
  const tail = 6 + count * 2;
  view.setInt32(tail, message.ack);
  view.setInt32(tail + 4, message.frame);
  view.setInt32(tail + 8, message.checksumFrame);
  view.setUint32(tail + 12, message.checksum >>> 0);
  view.setInt8(tail + 16, Math.max(-128, Math.min(127, message.lead | 0)));
  return new Uint8Array(view.buffer);
}

/** Разобрать пакет; null — мусор или чужой формат. */
export function decode(bytes: Uint8Array): NetMessage | null {
  if (bytes.byteLength < 1) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const type = view.getUint8(0);
  if (type === MESSAGE.hello) {
    if (bytes.byteLength !== 13) return null;
    return { type: "hello", version: view.getUint32(1), content: view.getUint32(5), seed: view.getInt32(9) };
  }
  if (type === MESSAGE.ping || type === MESSAGE.pong) {
    if (bytes.byteLength !== 5) return null;
    return { type: type === MESSAGE.ping ? "ping" : "pong", stamp: view.getUint32(1) };
  }
  if (type !== MESSAGE.inputs || bytes.byteLength < 6) return null;
  const count = view.getUint8(5);
  if (count > MAX_INPUTS_PER_PACKET || bytes.byteLength !== 23 + count * 2) return null;
  const start = view.getInt32(1);
  if (start < 0) return null;
  const inputs: number[] = [];
  for (let index = 0; index < count; index++) inputs.push(view.getUint16(6 + index * 2));
  const tail = 6 + count * 2;
  return {
    type: "inputs",
    start,
    inputs,
    ack: view.getInt32(tail),
    frame: view.getInt32(tail + 4),
    checksumFrame: view.getInt32(tail + 8),
    checksum: view.getUint32(tail + 12),
    lead: view.getInt8(tail + 16),
  };
}
